import { useState } from 'react'
import { useDrop } from 'react-dnd'
import { useDispatch, useSelector, useStore } from 'react-redux'
import toast from 'react-hot-toast'
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  IconButton,
  Stack,
  Avatar,
  Tooltip
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Check as CheckIcon,
  Clear as ClearIcon
} from '@mui/icons-material'
import { generateUUID, collectDroppedFileIds } from '@utils/helpers'
import { startCategorizing, finishCategorizing } from 'slices/global'
import { setFilterFiles } from '@slices/customerFiles'
import Image from './Image'
import styled from '@emotion/styled'

const PurchaseReceiptCard = ({
  key = generateUUID(),
  onDrop = () => { },
  files = [],
  label = '',
  haveOcr = 0,
  handleDeleteItem = () => { },
  count = 0,
  addIcon = false,
  deleteIcon = false,
  icons,
  removeItem,
  showBorder = false,
  addMore,
  text = '',
  icon = null,
  documentDeletewithfileLoader = false,
  ...rest
}) => {
  const dispatch = useDispatch()
  const store = useStore()
  const [deleteLoader, setDeleteLoader] = useState(false)
  const [progress, setProgress] = useState(false)
  const [progressValue, setProgressValue] = useState(0)
  const { customer_files } = useSelector(state => state)

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: deleteLoader ? [] : 'image',
    drop: async item => {
      const { id } = item?.id?.file_obj
      if (!progress && id && !deleteLoader) {
        const alreadyDroppedIds = collectDroppedFileIds(store.getState())
        const droppedRevId =
          item?.id?.file_obj?.document_revision_id ||
          item?.id?.document_revision_id ||
          null
        const isAlreadyCategorized =
          alreadyDroppedIds.has(id) ||
          (droppedRevId && alreadyDroppedIds.has(droppedRevId))

        if (isAlreadyCategorized) {
          // toast.error('This document is already categorized in another section.')
          return
        }

        setProgressValue(0)
        setProgress(true)
        dispatch(startCategorizing())

        const interval = setInterval(() => {
          setProgressValue(prev => {
            if (prev >= 99) {
              clearInterval(interval)
              return prev
            }
            return prev + 9
          })
        }, 500)

        try {
          await onDrop(item.id)
        } finally {
          clearInterval(interval)
          setProgressValue(100)
          dispatch(finishCategorizing())

          setTimeout(() => {
            setProgress(false)
            setProgressValue(0)
          }, 1000)
        }
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  })

  const isActive = isOver && canDrop

  const handleDelete = async ({ count = 0, index = 0, file = {} }) => {
    setDeleteLoader(true)

    try {
      await handleDeleteItem(count, index, file)
      // Restore the deleted file back to the library only if we have a valid id
      const restoredId = file?.file_obj?.id
      if (restoredId) {
        const alreadyExists = Array.isArray(customer_files?.files)
          ? customer_files.files.some(f => f?.file_obj?.id === restoredId)
          : false

        if (!alreadyExists) {
          const restoredFileEntry = {
            file_no: customer_files?.files?.length || 0,
            file: {},
            id: generateUUID(),
            fileName: file?.file_obj?.name || file?.fileName || '',
            status: 'File uploaded successfully.',
            progress: 100,
            isUploaded: true,
            isError: false,
            file_obj: file?.file_obj
          }

          dispatch(setFilterFiles({ file: restoredFileEntry }))
        }
      }
    } catch (error) {
      console.error('Delete Error:', error)
    } finally {
      setDeleteLoader(false)
    }
  }

  // const handleImagePreview = item => {
  //   dispatch(
  //     showImagePreview({
  //       name: item?.file_obj?.name || '',
  //       docId: item?.file_obj?.document_revision_id || '',
  //       preview: true
  //     })
  //   )
  // }

  const truncateFileName = (fileName, maxLength = 15) => {
    return fileName?.length > maxLength
      ? `${fileName.substring(0, maxLength)}...`
      : fileName
  }

  const renderFileItem = (item, index) => {
    const checkFile =
      item.hasOwnProperty('file_obj') &&
      (item?.name || item?.fileName || item.file_obj.name)
    return (
      <Box key={index} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            variant="rounded"
            sx={{
              width: 60,
              height: 60,
              cursor: 'pointer',
              bgcolor: 'grey.100'
            }}
          >
            {item.file_obj?.document_revision_id ||
              item?.document_revision_id ? (
              <Image
                item={item}
                url={
                  item?.file_obj?.document_revision_id ||
                  item?.document_revision_id
                }
                alt={item?.file_obj?.name || item?.name || ''}
                dragfalse={true}
                width={'100%'}
                height={'100%'}
              />
            ) : (
              <CloudUploadIcon />
            )}
          </Avatar>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="body2"
                sx={{
                  flexGrow: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                <FileName>
                  {truncateFileName(
                    item?.file_obj?.name || item?.name || item?.fileName || ''
                  )}
                </FileName>
              </Typography>

              {deleteIcon && (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete({ count, index, file: item })}
                    disabled={deleteLoader || documentDeletewithfileLoader}
                    sx={{ color: 'error.main' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {progress ? (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" sx={{ float: 'right', mt: 0.5 }}>
                  {progressValue}%
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Stack>
      </Box>
    )
  }

  return (
    <Box ref={drop} key={key} {...rest} sx={{ height: '100%', width: '100%' }}>
      <Card
        sx={{
          height: '100%',
          width: '100%',
          opacity: progress || deleteLoader ? 0.5 : 1,
          border: !progress && isActive ? '2px dashed' : 'none',
          borderColor: 'primary.main',
          pointerEvents: progress || deleteLoader ? 'none' : 'auto',
          transition: 'all 0.2s ease',
        }}
      >
        <CardContent>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography
              variant="h6"
              component="h5"
              sx={{
                textAlign: 'center',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                paddingTop: '5px'
              }}
            >
              {deleteIcon && removeItem}
              {label || text}
              {!!haveOcr && haveOcr !== 0 && (
                <OcrBadge>
                  OCR <CheckIcon sx={{ fontSize: 12 }} />
                </OcrBadge>
              )}
            </Typography>
            {icon}
          </Stack>

          {deleteLoader || documentDeletewithfileLoader ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Deleting {label}...
              </Typography>
            </Box>
          ) : files?.length > 0 ? (
            <Box>{files.map(renderFileItem)}</Box>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                color: 'text.secondary'
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography
                variant="body2"
                sx={{ color: '#777', fontSize: '12px' }}
              >
                Drop Image from top bar
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {addIcon && addMore && addMore}
    </Box>
  )
}

export default PurchaseReceiptCard

const FileName = styled.p`
  margin: 0;
  font-size: 12px;
  word-break: break-word;
  flex: 1;
`
const DragAndDropHeaderText = styled.h5`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
`

const OcrBadge = styled.span`
  background-color: #32cd32;
  color: white;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 8px;
  padding: 4px 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`
